<template>
  <div
    class="w-full max-w-2xl mx-auto bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden"
  >
    <!-- Header -->
    <div
      class="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50"
    >
      <div>
        <h3 class="text-lg sm:text-xl font-bold text-gray-900">
          Announcements
        </h3>
        <p class="text-xs sm:text-sm text-gray-500 mt-0.5">
          Latest school updates and notices
        </p>
      </div>

      <RouterLink
        to="/report"
        class="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
      >
        View All
      </RouterLink>
    </div>

    <!-- Empty State -->
    <div
      v-if="!latestAnnouncements.length && !loading"
      class="flex flex-col items-center justify-center py-14 px-6 text-center"
    >
      <div
        class="h-14 w-14 rounded-full bg-blue-100 flex items-center justify-center mb-4"
      >
        <AlertCircle class="h-7 w-7 text-blue-600" />
      </div>

      <h4 class="text-base font-semibold text-gray-800">No Announcements</h4>

      <p class="text-sm text-gray-500 mt-1">
        There are no announcements available right now.
      </p>
    </div>

    <!-- Loading -->
    <div
      v-if="loading"
      class="p-5 sm:p-6 space-y-4"
    >
      <div
        v-for="i in 3"
        :key="i"
        class="h-24 rounded-2xl bg-gray-100 animate-pulse"
      ></div>
    </div>

    <!-- Announcement List -->
    <div
      v-if="!loading && latestAnnouncements.length"
      class="p-4 sm:p-5 space-y-4"
    >
      <div
        v-for="announcement in latestAnnouncements"
        :key="announcement._id"
        class="group relative overflow-hidden rounded-2xl border transition-all duration-300 hover:shadow-md hover:-translate-y-1"
        :class="
          announcement.isPinned
            ? 'border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50'
            : 'border-yellow-200 bg-gradient-to-r from-yellow-50 to-orange-50'
        "
      >
        <!-- Accent Bar -->
        <div
          class="absolute left-0 top-0 h-full w-1.5"
          :class="announcement.isPinned ? 'bg-blue-500' : 'bg-yellow-500'"
        ></div>

        <div class="p-4 sm:p-5 flex gap-4">
          <!-- Icon -->
          <div
            class="flex-shrink-0 h-11 w-11 rounded-xl flex items-center justify-center"
            :class="announcement.isPinned ? 'bg-blue-100' : 'bg-yellow-100'"
          >
            <AlertCircle
              class="h-5 w-5"
              :class="
                announcement.isPinned ? 'text-blue-600' : 'text-yellow-600'
              "
            />
          </div>

          <!-- Content -->
          <div class="flex-1 min-w-0">
            <div
              class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
            >
              <div class="flex items-center gap-2 flex-wrap">
                <h4 class="text-sm sm:text-base font-semibold text-gray-900">
                  {{ announcement.title }}
                </h4>

                <span
                  v-if="announcement.isPinned"
                  class="px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-wide bg-blue-600 text-white"
                >
                  PINNED
                </span>
              </div>

              <p class="text-xs text-gray-500 whitespace-nowrap">
                {{ formatDate(announcement.createdAt) }}
              </p>
            </div>

            <p class="text-sm text-gray-600 mt-2 leading-relaxed line-clamp-3">
              {{ announcement.message }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { AlertCircle } from "lucide-vue-next";
  import { computed, onMounted } from "vue";
  import { useStore } from "vuex";

  const store = useStore();

  /* STORE STATE */
  const announcements = computed(() => store.state.announcements || []);
  const loading = computed(() => store.state.announcementsLoading);

  /* Latest announcements */
  const latestAnnouncements = computed(() => {
    const now = new Date();

    return announcements.value
      .filter((a) => !a.expiryDate || new Date(a.expiryDate) > now)
      .slice()
      .sort((a, b) => {
        if (a?.isPinned && !b?.isPinned) return -1;
        if (!a?.isPinned && b?.isPinned) return 1;

        return new Date(b?.createdAt) - new Date(a?.createdAt);
      })
      .slice(0, 3);
  });

  /* Fetch announcements */
  onMounted(() => {
    if (!announcements.value.length) {
      store.dispatch("fetchAnnouncements");
    }
  });

  /* Date formatter */
  function formatDate(dateString) {
    if (!dateString) return "";

    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }
</script>
